import type { NextWebVitalsMetric } from "next/app";

const rumAccumulator: Record<string, unknown> = {};
let rumFlushed = false;

function flushRum() {
    if (rumFlushed || Object.keys(rumAccumulator).length === 0) return;
    rumFlushed = true;

    if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.log("[Web Vitals] Flushing RUM:", rumAccumulator);
    }

    const meta = window.__rumMeta;
    const body = JSON.stringify({
        rum: { ...rumAccumulator },
        ...(meta && {
            domainId: meta.domainId,
            domainName: meta.domainName,
            visitId: meta.visitId,
            visitorId: meta.visitorId,
            trafficSourceNetwork: meta.trafficSourceNetwork ?? null,
        }),
    });

    if (navigator.sendBeacon) {
        navigator.sendBeacon(
            "/api/rum",
            new Blob([body], { type: "application/json" }),
        );
        return;
    }

    void fetch("/api/rum", {
        method: "POST",
        body,
        headers: { "Content-Type": "application/json" },
        keepalive: true,
    });
}

if (typeof window !== "undefined") {
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") flushRum();
    });
    window.addEventListener("pagehide", flushRum);
}

const METRIC_KEY_MAP: Record<string, string> = {
    LCP: "lcpMs",
    CLS: "clsMilliScore",
    INP: "inpMs",
    FID: "fidMs",
    FCP: "fcpMs",
    TTFB: "ttfbMs",
    "Next.js-hydration": "hydrationMs",
    "Next.js-route-change-to-render": "routeChangeToRenderMs",
    "Next.js-render": "renderMs",
};

const METRIC_ACCUMULATOR_KEY: Record<string, string> = {
    "Next.js-hydration": "NextHydration",
    "Next.js-route-change-to-render": "NextRouteChangeToRender",
    "Next.js-render": "NextRender",
};

export function reportWebVitals(metric: NextWebVitalsMetric) {
    const { id, name, label, value } = metric;
    const extras = metric as {
        delta?: number;
        entries?: unknown[];
        navigationType?: string;
    };
    const ms = Math.round(name === "CLS" ? value * 1000 : value);
    const base = {
        metricId: id,
        label,
        page: window.location.pathname,
        timestamp: Date.now(),
    };
    const key = METRIC_ACCUMULATOR_KEY[name] ?? name;
    const valueKey = METRIC_KEY_MAP[name] ?? "value";

    rumAccumulator[key] = {
        [valueKey]: ms,
        ...base,
        ...(name === "CLS" && { clsScore: value }),
        ...((name === "LCP" || name === "CLS" || name === "INP") && {
            entriesCount: extras.entries?.length ?? 0,
        }),
        ...(name === "FID" && {
            fidDeltaMs: Math.round(extras.delta ?? value),
        }),
        ...(name === "TTFB" && {
            navigationType: extras.navigationType ?? "unknown",
        }),
    };

    if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.log(`[Web Vitals] ${name}: ${ms} (id: ${id})`);
    }
}

export function setRumMeta(meta: {
    domainId: number;
    domainName: string;
    visitId: string;
    visitorId: string;
    trafficSourceNetwork: string;
}) {
    if (typeof window !== "undefined") {
        window.__rumMeta = meta;
    }
}

export function setCaptureWebVitals(enabled: boolean) {
    if (typeof window !== "undefined") {
        window.__captureWebVitals = enabled;
    }
}
